import { StateCreator } from 'zustand';
import { getPatients } from '../../services/api';
import type { PatientResponse as ApiPatientResponse } from '../../services/api/types.gen';

export interface Patient {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  status: 'Active' | 'Pending' | 'Inactive';
  lastVisit: string;
  nextVisit: string;
  diagnosis: string;
  mrn: string;
  risk: 'High' | 'Medium' | 'Low';
  // Backend fields
  first_name: string;
  last_name: string;
  date_of_birth: string;
  ssn?: string;
  emergency_contact?: string;
  emergency_phone?: string;
  needs_legal_representative: boolean;
  has_poa: boolean;
  is_test_patient?: boolean;
  // Imported-chart provenance (EMR-1177): source + timestamp are permanent;
  // hidden_at set means the badge was demoted and must not render.
  import_source?: string;
  imported_at?: string;
  import_badge_hidden_at?: string;
  insurance_name?: string;
  clinic_id?: string;
  clinic_name?: string;
  created_at: string;
  updated_at: string;
}

export interface PatientManagerQuery {
  page?: number;
  search?: string;
  clinic_id?: string | null;
  surname_initial?: string | null;
  show_inactive?: boolean;
  hide_test?: boolean;
  hide_imported_patients?: boolean;
  include_metadata?: boolean;
}

export interface PatientsSlice {
  patients: Patient[];
  isPatientsLoading: boolean;
  error: string | null;
  searchTerm: string;
  totalPatients: number;
  alphaCounts: Record<string, number>;
  currentPage: number;
  pageSize: number;
  clinicId: string | null;
  surnameInitial: string;
  showInactive: boolean;
  hideTest: boolean;
  patientManagerHideImportedPatients: boolean;
  setSearchTerm: (term: string) => void;
  setCurrentPage: (page: number) => void;
  setClinicId: (clinicId: string | null) => void;
  setSurnameInitial: (surnameInitial: string) => void;
  setShowInactive: (showInactive: boolean) => void;
  setHideTest: (hideTest: boolean) => void;
  setPatientManagerHideImportedPatients: (hide: boolean) => void;
  fetchPatients: (query?: PatientManagerQuery) => Promise<void>;
}

// Helper function to convert API response to frontend Patient format
const convertApiPatientToPatient = (
  apiPatient: ApiPatientResponse
): Patient => {
  if (!apiPatient) {
    console.error('Received null/undefined patient data');
    throw new Error('Invalid patient data received from API');
  }

  return {
    id: apiPatient.id || '',
    name: `${apiPatient.first_name || ''} ${apiPatient.last_name || ''}`.trim(),
    email: apiPatient.email || undefined,
    phone: apiPatient.phone || undefined,
    address: apiPatient.address || undefined,
    city: apiPatient.city || undefined,
    state: apiPatient.state || undefined,
    zip_code: apiPatient.zip_code || undefined,
    status: 'Active',
    lastVisit: '2024-01-15',
    nextVisit: '2024-01-22',
    diagnosis: 'General',
    mrn: apiPatient.mrn || 'N/A',
    risk: 'Low',
    first_name: apiPatient.first_name || '',
    last_name: apiPatient.last_name || '',
    date_of_birth: apiPatient.date_of_birth || '',
    ssn: apiPatient.ssn || undefined,
    emergency_contact: apiPatient.emergency_contact || undefined,
    emergency_phone: apiPatient.emergency_phone || undefined,
    needs_legal_representative: apiPatient.needs_legal_representative || false,
    has_poa: apiPatient.has_poa || false,
    is_test_patient: apiPatient.is_test_patient || false,
    import_source: apiPatient.import_source || undefined,
    imported_at: apiPatient.imported_at || undefined,
    import_badge_hidden_at: apiPatient.import_badge_hidden_at || undefined,
    insurance_name: apiPatient.insurance_name || undefined,
    clinic_id: apiPatient.clinic_id || undefined,
    clinic_name: apiPatient.clinic_name || undefined,
    created_at: apiPatient.created_at || '',
    updated_at: apiPatient.updated_at || '',
  };
};

// Monotonic id for in-flight fetchPatients calls — see the staleness guard
// inside fetchPatients.
let fetchPatientsSeq = 0;

export const patientsSlice: StateCreator<PatientsSlice> = (set, get) => ({
  patients: [],
  isPatientsLoading: false,
  error: null,
  searchTerm: '',
  totalPatients: 0,
  alphaCounts: {},
  currentPage: 1,
  pageSize: 50,
  clinicId: null,
  surnameInitial: '',
  showInactive: false,
  hideTest: true,
  patientManagerHideImportedPatients: true,

  setSearchTerm: (term: string) => {
    set({ searchTerm: term });
  },

  setCurrentPage: (currentPage: number) => {
    set({ currentPage });
  },

  setClinicId: (clinicId: string | null) => {
    set({ clinicId });
  },

  setSurnameInitial: (surnameInitial: string) => {
    set({ surnameInitial });
  },

  setShowInactive: (showInactive: boolean) => {
    set({ showInactive });
  },

  setHideTest: (hideTest: boolean) => {
    set({ hideTest });
  },

  setPatientManagerHideImportedPatients: (hide: boolean) => {
    set({ patientManagerHideImportedPatients: hide });
  },

  fetchPatients: async (query = {}) => {
    const state = get();
    const page = query.page ?? state.currentPage;
    const currentQuery = {
      page,
      search: query.search ?? state.searchTerm,
      // `null` deliberately clears an active server filter; only an omitted
      // key inherits the previous query state.
      clinic_id:
        query.clinic_id !== undefined ? query.clinic_id : state.clinicId,
      surname_initial:
        query.surname_initial !== undefined
          ? query.surname_initial
          : state.surnameInitial || null,
      show_inactive: query.show_inactive ?? state.showInactive,
      hide_test: query.hide_test ?? state.hideTest,
      hide_imported_patients:
        query.hide_imported_patients ??
        state.patientManagerHideImportedPatients,
      include_metadata: query.include_metadata ?? page === 1,
    };
    set({
      currentPage: currentQuery.page,
      clinicId: currentQuery.clinic_id,
      surnameInitial: currentQuery.surname_initial ?? '',
      searchTerm: currentQuery.search,
      showInactive: currentQuery.show_inactive,
      hideTest: currentQuery.hide_test,
      patientManagerHideImportedPatients: currentQuery.hide_imported_patients,
    });
    // Concurrent fetches (e.g. the mount fetch racing a filter-toggle fetch)
    // resolve in any order; only the newest request may write results, or a
    // stale response overwrites the filter the user just applied.
    const seq = ++fetchPatientsSeq;
    try {
      set({ isPatientsLoading: true, error: null });
      const response = await getPatients({
        query: currentQuery,
      });
      if (seq !== fetchPatientsSeq) {
        return;
      }
      if (response && response.data) {
        const patients = response.data.items.map(convertApiPatientToPatient);
        const metadata =
          response.data.total != null && response.data.alpha_counts != null
            ? {
                totalPatients: response.data.total,
                alphaCounts: response.data.alpha_counts,
              }
            : {};
        set({
          patients,
          ...metadata,
          currentPage: response.data.page,
          pageSize: response.data.page_size,
          isPatientsLoading: false,
        });
      } else {
        // The generated client doesn't throw on HTTP errors; without this
        // branch a failed load leaves stale data and no error signal.
        set({
          error: 'Failed to load patients. Please try again.',
          patients: [],
          totalPatients: 0,
          alphaCounts: {},
        });
      }
    } catch (error) {
      if (seq !== fetchPatientsSeq) {
        return;
      }
      console.error('Error fetching patients:', error);
      set({
        error: 'Failed to load patients. Please try again.',
        patients: [],
        totalPatients: 0,
        alphaCounts: {},
      });
    } finally {
      if (seq === fetchPatientsSeq) {
        set({ isPatientsLoading: false });
      }
    }
  },
});
